import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  ColumnDef,
  FilterFn,
  RowSelectionState,
  ColumnFiltersState,
  Table,
  Row,
  Cell,
} from '@tanstack/react-table';
import { Box, Checkbox, IconButton } from '@mui/material';
import { rankItem } from '@tanstack/match-sorter-utils';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';

import '../../assets/css/table-widget.css';
import { theme } from '../../configs/theme';
import { formatPrice } from '../../utils/formatPrice';
import ThemeButton from '../common/ThemeButton';
import { Dropdown } from '../common/Dropdown';
import { TableWidgetSkeleton } from './skeletons/TableWidgetSkeleton';
import { TextField } from '../common/TextField';
import { useFitViewportHeight } from '../../hooks/useFitViewportHeight';
import { compareSortValues } from '../../utils/sortValues';
import { formatTableRow } from './formatTableRow';

/**
 * Column configuration for the TableWidget component.
 */
export interface TableWidgetColumns {
  /** Unique identifier for the column */
  id: string;
  /** Display name for the column header */
  name?: string;
  /** Number of columns this column should span */
  columnSpan?: number;
  /** Text alignment for column cells */
  columnAlignment?: 'left' | 'center' | 'right';
  /** Dot-notation path to nested property (e.g., 'order.customer.name') */
  path?: string;
  /** Optional formatter applied to the raw cell value before display */
  formatter?: (value: unknown) => string;
}

/**
 * Props for the TableWidget component.
 *
 * @template T - The type of data items in the table
 */
type Props<T> = {
  /** Array of data items to display in the table */
  data: T[] | undefined;
  /** Column configuration array */
  columns: TableWidgetColumns[];
  /** Callback fired when a row is clicked */
  handleRowClick?: (row: T) => void;
  /** Content of a last, unnamed cell in each row, such as buttons. A click in it is not a row click. */
  renderRowActions?: (row: T) => ReactNode;
  /** Whether to enable row selection with checkboxes */
  enableRowSelection?: boolean;
  /** Callback fired when row selection changes, receives array of selected rows */
  onRowSelection?: (selectedRows: T[]) => void;
  /** Row id accessor used for stable row identity (required to use `defaultSelectedRowIds`) */
  getRowId?: (item: T) => string;
  /** Row ids (per `getRowId`) that should start out checked when `enableRowSelection` is set */
  defaultSelectedRowIds?: string[];
  /** Callback fired when the create button is clicked */
  createButtonClick?: () => void;
  /** Title text for the create button */
  createButtonTitle?: string;
  /** Callback fired when the secondary button is clicked */
  secondaryButtonClick?: () => void;
  /** Title text for the secondary button */
  secondaryButtonTitle?: string;
  nrOfRowsShowing?: number;
  isScrollable?: boolean;
  scrollMaxHeight?: string;
  infiniteScroll?: boolean;
  fillHeight?: boolean;
  /**
   * Lets the table end at the bottom of the viewport, for pages where the parent has no fixed
   * height (e.g. tabs on a detail page). Ignored when `fillHeight` is set.
   */
  fitViewport?: boolean;
  isUsingFilter?: boolean;
  headerStartContent?: ReactNode;
  headerEndContent?: ReactNode;
  autoScroll?: boolean;
  autoScrollIntervalMs?: number;

  buttonType?: 'primary' | 'secondary' | 'tertiary';
  /**
   * For a list the backend pages and searches: `data` holds the rows loaded so far, the search
   * box is the caller's, and scrolling to the end asks for the next page. The table then neither
   * searches, sorts nor filters by column itself, since it only has some of the rows.
   */
  serverPaging?: ServerPaging;
};

export type ServerPaging = {
  search: string;
  onSearchChange: (search: string) => void;
  /** Whether the rows for the current search are on their way */
  isSearching: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
};

/**
 * Fuzzy filter function for table search/filtering.
 *
 * Uses TanStack match-sorter to perform fuzzy matching on cell values.
 *
 * @param row - The table row being filtered
 * @param columnId - The column ID to filter on
 * @param value - The search/filter value
 * @returns `true` if the row matches the filter, `false` otherwise
 */
const fuzzyFilter: FilterFn<unknown> = (row, columnId, value) => {
  const cellValue = row.getValue(columnId);

  const itemRank = rankItem(String(cellValue ?? ''), value);
  return itemRank.passed;
};

const containsFilter: FilterFn<unknown> = (row, columnId, value) => {
  const cellValue = row.getValue(columnId);

  if (cellValue === null || cellValue === undefined) {
    return false;
  }

  return String(cellValue).toLowerCase().includes(String(value).toLowerCase());
};

const nonFilterableColumnIds = new Set(['select', 'previewBase64', 'checkbox']);

const rowActionsColumnId = 'rowActions';

/** A row with a status dot needs the status as text too, for the tooltip and the search. */
type RequireStatusText<T> = 'colorStatus' extends keyof T
  ? { statusInSwedish: string | undefined }
  : unknown;

/**
 * A feature-rich table component built on TanStack Table (React Table).
 *
 * Provides a data table with sorting, filtering, pagination, row selection,
 * and custom cell formatting. Supports various data types with automatic
 * formatting for prices, dates, times, and status indicators.
 *
 * @template T - The type of data items in the table
 *
 * @param props - Component props
 *
 * @remarks
 * - Uses fuzzy search for filtering across all columns
 * - Automatically formats special column types (price, date, time, status, etc.)
 * - Supports nested property access via `path` in column config
 * - Pagination shows when data has more than 10 items
 * - Row selection can be enabled via `enableRowSelection` prop
 * - Custom cell rendering for status indicators and checkboxes
 * - Shows skeleton loader when data is undefined
 */
export const TableWidget = <T extends RequireStatusText<T>>({
  data,
  columns,
  createButtonClick,
  handleRowClick,
  renderRowActions,
  enableRowSelection = false,
  onRowSelection,
  getRowId,
  defaultSelectedRowIds,
  createButtonTitle = 'Skapa',
  secondaryButtonClick,
  secondaryButtonTitle,
  nrOfRowsShowing = 10,
  scrollMaxHeight = '60vh',
  fillHeight = false,
  fitViewport = false,
  isUsingFilter = true,
  headerStartContent,
  headerEndContent,
  autoScroll = false,
  autoScrollIntervalMs = 3000,
  buttonType = 'primary',
  serverPaging,
}: Props<T>) => {
  const [filterText, setFilterText] = useState('');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [selectedColumnId, setSelectedColumnId] = useState('');
  const [isFilterStateHydrated, setIsFilterStateHydrated] = useState(false);
  const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);
  const [visibleScrollableRowsCount, setVisibleScrollableRowsCount] = useState(nrOfRowsShowing);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const hasSeededSelectionRef = useRef(false);
  const isScrollable = true;
  const infiniteScroll = true;
  const shouldFitViewport = fitViewport && !fillHeight;
  // Read through a ref so that a renderer made anew on each render does not rebuild the columns.
  const renderRowActionsRef = useRef(renderRowActions);
  renderRowActionsRef.current = renderRowActions;
  const hasRowActions = !!renderRowActions;

  const tableFilterStorageKey = useMemo(() => {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : 'unknown';
    // Tables on the same page can share column ids, so the headers keep their searches apart
    const columnsSignature = columns.map((column) => `${column.id}:${column.name ?? ''}`).join('|');

    return `table-widget-filter:${pathname}:${columnsSignature}`;
  }, [columns]);

  useEffect(() => {
    // A search the backend runs belongs to the caller, which keeps it
    if (typeof window === 'undefined' || serverPaging) {
      setIsFilterStateHydrated(true);
      return;
    }

    try {
      const savedFilterState = window.sessionStorage.getItem(tableFilterStorageKey);

      if (savedFilterState) {
        const parsedFilterState = JSON.parse(savedFilterState) as {
          filterText?: string;
          selectedColumnId?: string;
        };

        setFilterText(parsedFilterState.filterText ?? '');
        setSelectedColumnId(parsedFilterState.selectedColumnId ?? '');
      }
    } catch {
      // Ignore storage parse/read errors and continue with default state
    } finally {
      setIsFilterStateHydrated(true);
    }
  }, [tableFilterStorageKey]);

  useEffect(() => {
    if (!isFilterStateHydrated || typeof window === 'undefined' || serverPaging) return;

    try {
      if (!filterText && !selectedColumnId) {
        window.sessionStorage.removeItem(tableFilterStorageKey);
        return;
      }

      window.sessionStorage.setItem(
        tableFilterStorageKey,
        JSON.stringify({ filterText, selectedColumnId })
      );
    } catch {
      // Ignore storage write/remove errors
    }
  }, [filterText, selectedColumnId, tableFilterStorageKey, isFilterStateHydrated]);

  const filterableColumns = useMemo(
    () => columns.filter((col) => !nonFilterableColumnIds.has(col.id)),
    [columns]
  );

  const formattedRows = useMemo(
    () => data?.map((item) => formatTableRow(item as Record<string, unknown>, columns)),
    [data, columns]
  );

  const filteredData = useMemo(
    () => formattedRows?.map(({ row }) => row as Partial<T>),
    [formattedRows]
  );

  // The table works on the formatted rows, which only hold the columns' fields, so callbacks get
  // the caller's own item instead. TanStack keeps each row's index into `data` through sorting
  // and filtering.
  const itemAt = (index: number): T => (data ?? [])[index];

  // Define columns for the table
  const columnHeaders = useMemo<ColumnDef<Partial<T>>[]>(
    () => [
      // Add checkbox column if selection is enabled
      ...(enableRowSelection
        ? [
            {
              id: 'select',
              header: ({ table }: { table: Table<Partial<T>> }) => (
                <Checkbox
                  checked={table.getIsAllRowsSelected()}
                  onChange={table.getToggleAllRowsSelectedHandler()}
                  sx={{
                    padding: '0',
                    '&.Mui-checked': {
                      color: theme.palette.customColors.emerald,
                    },
                  }}
                />
              ),
              cell: ({ row }: { row: Row<Partial<T>> }) => (
                <Checkbox
                  disabled={!row.getCanSelect()}
                  checked={row.getIsSelected()}
                  onChange={row.getToggleSelectedHandler()}
                  onClick={(e) => e.stopPropagation()} // Prevent row click when clicking checkbox />
                  sx={{
                    padding: '0',
                    '&.Mui-checked': {
                      color: theme.palette.customColors.emerald,
                    },
                  }}
                />
              ),
            },
          ]
        : []),
      // Existing columns
      ...columns.map((header) => ({
        accessorKey: header.id,
        header: header.name,
        enableSorting: !serverPaging,
        sortingFn: (rowA: Row<Partial<T>>, rowB: Row<Partial<T>>, columnId: string) =>
          compareSortValues(
            formattedRows?.[rowA.index]?.sortValues[columnId] ?? null,
            formattedRows?.[rowB.index]?.sortValues[columnId] ?? null
          ),
        enableColumnFilter: !nonFilterableColumnIds.has(header.id),
        filterFn: (selectedColumnId ? containsFilter : fuzzyFilter) as FilterFn<Partial<T>>,
      })),
      ...(hasRowActions
        ? [
            {
              id: rowActionsColumnId,
              header: '',
              enableSorting: false,
              enableColumnFilter: false,
              cell: ({ row }: { row: Row<Partial<T>> }) =>
                renderRowActionsRef.current?.(itemAt(row.index)),
            },
          ]
        : []),
    ],
    [columns, enableRowSelection, selectedColumnId, formattedRows, hasRowActions]
  );

  const table = useReactTable<Partial<T>>({
    data: filteredData ?? [],
    columns: columnHeaders,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: fuzzyFilter as FilterFn<Partial<T>>,
    state: {
      globalFilter: selectedColumnId || serverPaging ? '' : filterText,
      rowSelection,
      columnFilters,
    },
    // A row whose item has "checkboxDisabled" set to true cannot be selected
    enableRowSelection: (row) =>
      !(itemAt(row.index) as { checkboxDisabled?: boolean }).checkboxDisabled,
    getRowId: getRowId ? (_row, index) => getRowId(itemAt(index)) : undefined,
    onRowSelectionChange: setRowSelection, // Handle row selection changes
    onGlobalFilterChange: setFilterText,
    onColumnFiltersChange: setColumnFilters,
    initialState: {
      pagination: {
        pageSize: nrOfRowsShowing,
      },
      sorting: [], // Initial sorting state
    },
  });

  useEffect(() => {
    if (onRowSelection) {
      const selectedRows = table.getSelectedRowModel().rows.map((row) => itemAt(row.index));
      onRowSelection(selectedRows);
    }
  }, [rowSelection]);

  // Seed initial checkbox state from `defaultSelectedRowIds` once data (and stable row ids) are
  // available. Guarded to run once so it doesn't fight with the user's own selection changes.
  useEffect(() => {
    if (hasSeededSelectionRef.current) return;
    if (!getRowId || !defaultSelectedRowIds || !filteredData) return;

    hasSeededSelectionRef.current = true;
    setRowSelection(Object.fromEntries(defaultSelectedRowIds.map((id) => [id, true])));
  }, [filteredData, getRowId, defaultSelectedRowIds]);

  useEffect(() => {
    if (isScrollable || !autoScroll) return;

    const pageCount = table.getPageCount();
    const currentPage = table.getState().pagination.pageIndex;

    if (pageCount > 0 && currentPage >= pageCount) {
      table.setPageIndex(0);
    }
  }, [autoScroll, isScrollable, table, filteredData]);

  useEffect(() => {
    if (isScrollable || !autoScroll || isAutoScrollPaused) return;

    const pageCount = table.getPageCount();
    if (pageCount <= 1) return;

    const intervalId = window.setInterval(() => {
      const currentPage = table.getState().pagination.pageIndex;
      const nextPage = currentPage + 1 >= pageCount ? 0 : currentPage + 1;
      table.setPageIndex(nextPage);
    }, autoScrollIntervalMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [autoScroll, autoScrollIntervalMs, isAutoScrollPaused, isScrollable, table, filteredData]);

  useEffect(() => {
    if (!selectedColumnId || !filterText.trim()) {
      setColumnFilters([]);
      return;
    }

    setColumnFilters([
      {
        id: selectedColumnId,
        value: filterText,
      },
    ]);
  }, [selectedColumnId, filterText]);

  useEffect(() => {
    if (isScrollable && infiniteScroll) {
      setVisibleScrollableRowsCount(nrOfRowsShowing);
    }
  }, [isScrollable, infiniteScroll, nrOfRowsShowing, filteredData]);

  useEffect(() => {
    if (!isScrollable || !infiniteScroll || !wrapperRef.current) return;

    const maybeFillScrollableArea = () => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;

      const totalRows = table.getPrePaginationRowModel().rows.length;
      if (visibleScrollableRowsCount >= totalRows) return;

      const hasOverflow = wrapper.scrollHeight > wrapper.clientHeight + 1;
      // If the container isn't scrollable yet, keep loading in chunks until it is.
      if (!hasOverflow) {
        setVisibleScrollableRowsCount((prev) => Math.min(prev + nrOfRowsShowing, totalRows));
      }
    };

    maybeFillScrollableArea();

    const wrapper = wrapperRef.current;
    const observer = new ResizeObserver(maybeFillScrollableArea);
    observer.observe(wrapper);

    const parent = sectionRef.current?.parentElement;
    if (parent) {
      observer.observe(parent);
      parent.addEventListener('transitionend', maybeFillScrollableArea);
    }

    return () => {
      observer.disconnect();
      parent?.removeEventListener('transitionend', maybeFillScrollableArea);
    };
  }, [
    isScrollable,
    infiniteScroll,
    visibleScrollableRowsCount,
    nrOfRowsShowing,
    filteredData,
    table,
  ]);

  useEffect(() => {
    if (!fillHeight || !sectionRef.current || !wrapperRef.current) return;

    const recalc = () => {
      const section = sectionRef.current;
      const wrapper = wrapperRef.current;
      if (!section || !wrapper) return;
      const parent = section.parentElement;
      const counterReserve = isScrollable && infiniteScroll ? 24 : 0;
      const wrapperTop = wrapper.getBoundingClientRect().top;
      const parentBottom =
        parent?.getBoundingClientRect().bottom ?? section.getBoundingClientRect().bottom;
      const parentAvailable = parentBottom - wrapperTop - counterReserve;
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const viewportAvailable = viewportHeight - wrapperTop - 12 - counterReserve;

      const clampedAvailable = Math.max(0, Math.min(parentAvailable, viewportAvailable));

      // Keep the counter close to the table when there are only a few rows,
      // but still cap by the available viewport space.
      const contentHeight = wrapper.scrollHeight;
      const targetHeight =
        isScrollable && infiniteScroll
          ? Math.min(clampedAvailable, contentHeight)
          : clampedAvailable;

      wrapper.style.height = `${targetHeight}px`;
      wrapper.style.maxHeight = `${clampedAvailable}px`;
    };

    recalc();
    requestAnimationFrame(recalc);

    const observer = new ResizeObserver(recalc);
    observer.observe(sectionRef.current);
    // also observe parent so accordion changes are caught
    const parent = sectionRef.current.parentElement;
    if (parent) observer.observe(parent);
    window.addEventListener('resize', recalc);
    window.visualViewport?.addEventListener('resize', recalc);
    parent?.addEventListener('transitionend', recalc);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', recalc);
      window.visualViewport?.removeEventListener('resize', recalc);
      parent?.removeEventListener('transitionend', recalc);
    };
  }, [
    fillHeight,
    infiniteScroll,
    isScrollable,
    filteredData,
    visibleScrollableRowsCount,
    nrOfRowsShowing,
  ]);

  useFitViewportHeight(sectionRef, wrapperRef, shouldFitViewport && data !== undefined);

  // The first page may not fill the table, and then there is nothing to scroll to the end of
  useEffect(() => {
    if (!serverPaging?.hasMore || serverPaging.isLoadingMore || !wrapperRef.current) return;

    const frame = requestAnimationFrame(() => {
      const wrapper = wrapperRef.current;
      if (wrapper && wrapper.scrollHeight <= wrapper.clientHeight + 1) {
        serverPaging.onLoadMore();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [data, serverPaging?.hasMore, serverPaging?.isLoadingMore]);

  const searchText = serverPaging ? serverPaging.search : filterText;
  const setSearchText = serverPaging ? serverPaging.onSearchChange : setFilterText;

  const clearSearchAndStorage = () => {
    setSearchText('');
    setSelectedColumnId('');
    setColumnFilters([]);

    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.removeItem(tableFilterStorageKey);
      } catch {
        // Ignore storage remove errors
      }
    }
  };

  /**
   * Gets the column span size for a given column ID.
   *
   * @param columnId - The column ID to get span size for
   * @returns The column span size, or 1 if not specified
   */
  const colSpanSize = (columnId: string) => {
    const column = columns.find((col) => col.id === columnId);
    return column ? column.columnSpan : 1;
  };

  /**
   * Calculates the total column span for all visible columns.
   *
   * Includes the selection checkbox column if row selection is enabled.
   *
   * @returns Total number of columns (accounting for spans) plus selection column if enabled
   */
  const getTotalColumnSpan = () => {
    return (
      columns.reduce((sum, col) => sum + (col.columnSpan || 1), 0) +
      (enableRowSelection ? 1 : 0) +
      (hasRowActions ? 1 : 0)
    );
  };

  /**
   * Gets the CSS class name for column alignment.
   *
   * @param columnId - The column ID to get alignment for
   * @returns CSS class name for alignment (e.g., 'table-cell-align-left')
   */
  const getColumnAlignment = (columnId: string) => {
    const column = columns.find((col) => col.id === columnId);
    return column && column.columnAlignment
      ? `table-cell-align-${column.columnAlignment}`
      : 'table-cell-align-left';
  };

  /**
   * Gets the title attribute value for a table cell.
   *
   * Returns the cell value as a string for tooltip display, with special
   * handling for select and colorStatus columns.
   *
   * @param cell - The table cell to get title for
   * @returns Title string for the cell, or undefined for select column
   */
  const cellTitle = (cell: Cell<Partial<T>, unknown>) => {
    const cellId = cell.column.id;
    if (cellId === 'select' || cellId === 'previewBase64' || cellId === rowActionsColumnId) {
      return undefined;
    } else {
      return cell.getValue() as string;
    }
  };

  const getPreviewSrc = (rowData: Record<string, unknown>): string => {
    const previewBase64 = rowData.previewBase64;

    if (typeof previewBase64 !== 'string' || !previewBase64) {
      return '';
    }

    if (previewBase64.startsWith('data:')) {
      return previewBase64;
    }

    const contentType = typeof rowData.contentType === 'string' ? rowData.contentType : '';
    const mimeType = contentType.startsWith('image/') ? contentType : 'image/jpeg';

    return `data:${mimeType};base64,${previewBase64}`;
  };

  if (!data) {
    return <TableWidgetSkeleton />;
  }

  const prePaginationRows = table.getPrePaginationRowModel().rows;
  const visibleRows = serverPaging
    ? prePaginationRows
    : isScrollable
      ? infiniteScroll
        ? prePaginationRows.slice(0, visibleScrollableRowsCount)
        : prePaginationRows
      : table.getRowModel().rows;

  const parseNumericValue = (value: string): number => {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    // Normalize unicode minus and whitespace, convert decimal comma to dot, strip non-numeric characters except sign and dot
    // Normalizing unicode minus is needed for the footer to calculate the total sum correctly
    const cleanedValue = value
      .replace(/\u2212/g, '-')
      .replace(/\s/g, '')
      .replace(',', '.')
      .replace(/[^0-9.-]/g, '');

    const parsedValue = Number(cleanedValue);

    return Number.isFinite(parsedValue) ? parsedValue : 0;
  };

  const summaColumnId = columns.find((column) => column.name?.trim().toLowerCase() === 'summa')?.id;

  const totalSummaValue =
    summaColumnId === undefined
      ? null
      : prePaginationRows.reduce(
          (sum, row) => sum + parseNumericValue(row.getValue(summaColumnId)),
          0
        );

  const formattedTotalSummaValue =
    totalSummaValue === null
      ? ''
      : formatPrice({
          countryCode: 'sv-SE',
          currency: 'SEK',
          value: totalSummaValue,
        });

  const hasActiveFilter = searchText.length > 1 || columnFilters.length > 0;

  return (
    <section
      ref={sectionRef}
      className="table-default-section"
      style={
        fillHeight ? { flex: 1, minHeight: 0, maxHeight: '100%', overflow: 'hidden' } : undefined
      }
      onMouseEnter={() => {
        if (autoScroll) setIsAutoScrollPaused(true);
      }}
      onMouseLeave={() => {
        if (autoScroll) setIsAutoScrollPaused(false);
      }}
    >
      <div className="table-default-section-header">
        <Box
          sx={{
            display: 'flex',
            gap: '1rem',
            alignItems: 'center',
            width: headerStartContent ? '70%' : '50%',
            minWidth: 0,
          }}
        >
          {headerStartContent && (
            <Box sx={{ minWidth: 0, flexShrink: 0 }}>{headerStartContent}</Box>
          )}

          {isUsingFilter && (
            <Box
              sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flex: 1, minWidth: 0 }}
            >
              <Box sx={{ flex: 3, minWidth: 0 }}>
                <TextField
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Sök"
                  icon={
                    searchText ? (
                      <IconButton
                        size="small"
                        aria-label="Rensa sök"
                        onClick={clearSearchAndStorage}
                        edge="end"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ) : null
                  }
                  iconAlign="right"
                />
              </Box>
              {!serverPaging && (
                <Box sx={{ flex: 2, minWidth: 0 }}>
                  <Dropdown
                    value={selectedColumnId}
                    onChange={(e) => setSelectedColumnId(e.target.value)}
                    data={[
                      { title: 'Välj kolumn', value: '' },
                      ...filterableColumns.map((column) => ({
                        title: column.name || column.id,
                        value: column.id,
                      })),
                    ]}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {headerEndContent && <Box sx={{ flexShrink: 0 }}>{headerEndContent}</Box>}
          {secondaryButtonClick && (
            <ThemeButton buttonType="tertiary" onClick={secondaryButtonClick}>
              {secondaryButtonTitle}
            </ThemeButton>
          )}
          {createButtonClick && (
            <ThemeButton onClick={createButtonClick} buttonType={buttonType}>
              {createButtonTitle}
            </ThemeButton>
          )}
        </Box>
      </div>

      <div
        ref={isScrollable ? wrapperRef : undefined}
        className={`table-default-wrapper ${isScrollable ? (fillHeight ? 'table-default-wrapper-fill' : 'table-default-wrapper-scrollable') : ''}`}
        style={
          isScrollable && !fillHeight && !shouldFitViewport
            ? { maxHeight: scrollMaxHeight }
            : undefined
        }
        onScroll={(event) => {
          if (!isScrollable || !infiniteScroll) return;

          const target = event.currentTarget;
          const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 20;

          if (serverPaging) {
            if (reachedBottom && serverPaging.hasMore && !serverPaging.isLoadingMore) {
              serverPaging.onLoadMore();
            }
            return;
          }

          if (reachedBottom && visibleScrollableRowsCount < prePaginationRows.length) {
            setVisibleScrollableRowsCount((prev) =>
              Math.min(prev + nrOfRowsShowing, prePaginationRows.length)
            );
          }
        }}
      >
        <table className="table-default">
          <thead className="table-head">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="table-row-head">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="table-head-cell table-cell"
                    scope="col"
                    colSpan={colSpanSize(header.id)}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className={`table-head-cell-icon ${getColumnAlignment(header.id)}`}>
                      {header.isPlaceholder ? null : (
                        <>
                          <p
                            className="table-head-cell-text"
                            title={
                              typeof header.column.columnDef.header === 'string'
                                ? header.column.columnDef.header
                                : undefined
                            }
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </p>

                          {header.column.getIsSorted() ? (
                            header.column.getIsSorted() === 'asc' ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )
                          ) : (
                            ''
                          )}
                        </>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody className="table-body">
            {serverPaging && !data.length ? (
              <tr>
                <td className="table-body-cell-empty" colSpan={getTotalColumnSpan()}>
                  {serverPaging.isSearching
                    ? 'Söker …'
                    : searchText.trim()
                      ? 'Inga matchande resultat hittades'
                      : 'Ingen data tillgänglig'}
                </td>
              </tr>
            ) : !data.length ? (
              <tr>
                <td className="table-body-cell-empty" colSpan={getTotalColumnSpan()}>
                  Ingen data tillgänglig
                </td>
              </tr>
            ) : hasActiveFilter && visibleRows.length === 0 ? (
              <tr>
                <td className="table-body-cell-empty" colSpan={getTotalColumnSpan()}>
                  Inga matchande resultat hittades
                </td>
              </tr>
            ) : (
              visibleRows.map((row) => (
                <tr
                  key={row.id}
                  className={`table-body-row ${handleRowClick ? 'table-body-row-clickable' : ''}`}
                  onClick={() => handleRowClick?.(itemAt(row.index))}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={`table-body-cell table-cell ${getColumnAlignment(cell.column.id)}`}
                      title={cellTitle(cell)}
                      colSpan={colSpanSize(cell.column.id)}
                      onClick={
                        cell.column.id === rowActionsColumnId
                          ? (event) => event.stopPropagation()
                          : undefined
                      }
                    >
                      {cell.column.id === 'select' || cell.column.id === rowActionsColumnId ? (
                        flexRender(cell.column.columnDef.cell, cell.getContext())
                      ) : cell.column.id === 'colorStatus' ? (
                        <span
                          className={`table-status-item status-item-${(cell.row.original as Record<string, unknown>)['colorStatusClass']}`}
                        />
                      ) : cell.column.id === 'previewBase64' ? (
                        getPreviewSrc(cell.row.original as Record<string, unknown>) ? (
                          <img
                            src={getPreviewSrc(cell.row.original as Record<string, unknown>)}
                            alt="Förhandsvisning"
                            loading="lazy"
                            decoding="async"
                            style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'contain' }}
                          />
                        ) : (
                          '-'
                        )
                      ) : cell.getValue() !== null && cell.getValue() !== undefined ? (
                        flexRender(cell.column.columnDef.cell, cell.getContext())
                      ) : (
                        (cell.getValue() as ReactNode)
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          {summaColumnId && (
            <tfoot className="table-foot">
              <tr>
                {table.getVisibleLeafColumns().map((column) => (
                  <td
                    key={column.id}
                    className={`table-cell table-foot-cell ${getColumnAlignment(column.id)}`}
                    colSpan={colSpanSize(column.id)}
                  >
                    {column.id === summaColumnId ? formattedTotalSummaValue : ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {isScrollable && infiniteScroll && prePaginationRows.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            color: 'text.secondary',
            fontSize: '0.8rem',
            padding: '0.2rem 0.25rem 0',
          }}
        >
          {serverPaging
            ? `Visar ${visibleRows.length}${serverPaging.isLoadingMore ? ', laddar fler' : ''}`
            : `Visar ${visibleRows.length} av ${prePaginationRows.length}`}
        </Box>
      )}
    </section>
  );
};
